import {inject} from '@angular/core';
import {DestroyRef} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Router} from '@angular/router';
import {AppConfigService} from '../Services/app-config.service';
import {PagerStateModel} from '../DataModels/pager-state-model';
import {PagerStateService} from '../Services/pager-state.service';

/**
 * Abstract base class for all server-side paged list components.
 * Centralises the pager state fields, the paginate() method, and pager restore logic
 * so that individual list components only need to implement their data-fetch method.
 *
 * Usage:
 *   export class MyListComponent extends ListPageBase {
 *     protected get pagerKey() { return this.pagerStatus.buildKey(this.router.url); }
 *     protected fetchPage() { this.getPagedApiData(); }
 *   }
 */
export abstract class ListPageBase {

  // These services are injected here so subclasses don't need to redeclare them
  // for paging purposes. Subclasses may still inject their own services normally.
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly appConfig = inject(AppConfigService);
  protected readonly pagerStatus = inject(PagerStateService);
  protected readonly router = inject(Router);

  // Pager state fields — owned here, not in each component
  page = 1;
  pageSize = this.appConfig.ListPageSize;
  startIndex = 1;
  endIndex = 0;
  totalRecords = 0;
  pageSizeOptions: number[] = this.appConfig.gridPageSizeSelection;

  /**
   * The cache key used to store/restore pager state for this list.
   * Override to include subLedger or other discriminators:
   *   protected get pagerKey() {
   *     return this.pagerStatus.buildKey(this.router.url, { subLedger: this.vm.subLedger });
   *   }
   */
  protected get pagerKey(): string {
    return this.pagerStatus.buildKey(this.router.url);
  }

  /**
   * Called by paginate() when withDataFetch is true.
   * Implement this in the subclass to trigger the API call for the current page.
   */
  protected abstract fetchPage(): void;

  /**
   * Restores pager state from the cache (sessionStorage / in-memory).
   * Call this once in ngOnInit before the first data fetch.
   */
  protected restorePagerState(extras?: Record<string, any>): void {
    const key = extras ? this.pagerStatus.buildKey(this.router.url, extras) : this.pagerKey;
    const restored = this.pagerStatus.restoreState(key);
    if (restored && restored.totalRecords !== 0) {
      this.page = restored.lastPage;
      this.pageSize = restored.lastPageSize;
      this.startIndex = restored.startIndex;
      this.endIndex = restored.endIndex;
    }
  }

  /**
   * Recalculates startIndex/endIndex, persists pager state, and optionally
   * triggers a data fetch. Call after every API response with withDataFetch=false,
   * and from the template page-change handler with withDataFetch=true.
   */
  paginate(withDataFetch: boolean): void {
    if (this.totalRecords === 0) {
      return;
    }

    this.startIndex = (this.page - 1) * this.pageSize + 1;
    this.endIndex = Math.min(this.page * this.pageSize, this.totalRecords);

    const model = new PagerStateModel();
    model.lastPage = this.page;
    model.lastPageSize = this.pageSize;
    model.totalRecords = this.totalRecords;
    model.dateTime = +new Date();
    model.startIndex = this.startIndex;
    model.endIndex = this.endIndex;

    this.pagerStatus.storeState(this.pagerKey, model);

    if (withDataFetch) {
      this.fetchPage();
    }
  }
}
